import { X } from "lucide-react";
import { useEffect } from "react";
import type { ReactNode } from "react";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    children: ReactNode;
    footer?: ReactNode;
}

/**
 * Dialog tengah sederhana untuk kebutuhan "pilih sesuatu" (picker).
 * Beda dengan `Drawer` yang menyamping — modal dipakai bila pilihannya
 * pendek dan tidak butuh konteks halaman di belakangnya.
 */
export function Modal({ open, onClose, title, description, children, footer }: ModalProps) {
    useEffect(() => {
        if (!open) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };

        window.addEventListener("keydown", onKeyDown);

        // Kunci scroll body selama modal terbuka.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <button
                type="button"
                aria-label="Close dialog"
                onClick={onClose}
                className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
            />

            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="app-shell-panel relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl border app-shell-border shadow-xl"
            >
                <header className="flex items-start justify-between gap-4 border-b app-shell-border p-5">
                    <div className="min-w-0">
                        <h2 className="app-text-primary text-lg font-semibold">{title}</h2>
                        {description && (
                            <p className="app-shell-text-muted mt-0.5 text-sm">{description}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="app-shell-text-muted rounded-lg p-1.5 transition-colors hover:app-content-subtle-hover"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div className="app-scroll flex-1 overflow-y-auto p-5">{children}</div>

                {footer && (
                    <footer className="border-t app-shell-border p-5">{footer}</footer>
                )}
            </div>
        </div>
    );
}
